import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Loader2, 
  Upload, 
  FileText, 
  CheckCircle2, 
  XCircle,
  ArrowLeft,
  Copy,
  ExternalLink,
  AlertCircle,
  User,
  Key,
  Link as LinkIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "usa", label: "USA" },
  { value: "canada", label: "Canada" },
  { value: "jamaica", label: "Jamaica" },
  { value: "barbados", label: "Barbados" },
  { value: "trinidad_tobago", label: "Trinidad & Tobago" },
  { value: "panama", label: "Panama" }
];

const ASSESSMENT_TYPES = [
  { value: "self_assessment", label: "Self Assessment" },
  { value: "technical", label: "Technical Assessment" },
  { value: "manager_validation", label: "Manager Validation" },
  { value: "simulation", label: "Hands-On Simulation" }
];

export default function AdminUpload() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Parsed document state
  const [parsedFile, setParsedFile] = useState(null);
  const [parsedProfile, setParsedProfile] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedAssessmentType, setSelectedAssessmentType] = useState("");
  const [creating, setCreating] = useState(false);

  const getAuthHeaders = () => ({
    headers: { 
      Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
    }
  });

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect({ target: { files } });
    }
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.docx')) {
      toast.error("Please select a .docx file");
      return;
    }

    // Parse the file first to show preview
    setParsedFile(file);
    setParsedProfile(null);
    setSelectedRegion("");
    setSelectedAssessmentType("");
    setUploadResults([]);
    
    // Show the file is selected - we'll create assessment when user clicks button
    toast.success(`File "${file.name}" selected. Please choose region and assessment type.`);
  };

  const handleCreateAssessment = async () => {
    if (!parsedFile || !selectedRegion || !selectedAssessmentType) {
      toast.error("Please select region and assessment type");
      return;
    }

    try {
      setCreating(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append("file", parsedFile);
      formData.append("region", selectedRegion);
      formData.append("assessment_type", selectedAssessmentType);
      
      const response = await axios.post(
        `${API}/admin/upload-assessment`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      
      setUploadResults([{
        filename: parsedFile.name,
        success: true,
        ...response.data
      }]);
      
      // Clear form
      setParsedFile(null);
      setSelectedRegion("");
      setSelectedAssessmentType("");
      
      toast.success("Assessment created successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      } else {
        toast.error(err.response?.data?.detail || "Upload failed");
        setUploadResults([{
          filename: parsedFile.name,
          success: false,
          error: err.response?.data?.detail || "Upload failed"
        }]);
      }
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/assessment/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Assessment link copied!");
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getRegionLabel = (value) => {
    return REGIONS.find(r => r.value === value)?.label || value;
  };

  const getTypeLabel = (value) => {
    return ASSESSMENT_TYPES.find(t => t.value === value)?.label || value;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky-header py-4 px-6 bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin" 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="text-lg font-bold text-[#003366] font-['Manrope'] hidden sm:inline">
              Upload Assessments
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-8 px-6">
        {/* Upload Zone */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-[#003366]">Upload Assessment Document</CardTitle>
            <CardDescription>
              Upload a personalized .docx assessment file, then select region and assessment type
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* File Drop Zone */}
            <div
              className={`upload-zone ${dragOver ? 'drag-over' : ''} ${parsedFile ? 'border-green-500 bg-green-50' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !parsedFile && document.getElementById('file-input').click()}
              data-testid="upload-zone"
            >
              {parsedFile ? (
                <div className="space-y-2">
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                  <p className="text-lg font-medium text-green-700">{parsedFile.name}</p>
                  <p className="text-sm text-muted-foreground">File ready for upload</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setParsedFile(null);
                      setSelectedRegion("");
                      setSelectedAssessmentType("");
                    }}
                    className="mt-2"
                  >
                    Choose Different File
                  </Button>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-[#003366]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-[#003366]" />
                  </div>
                  <p className="text-lg font-medium text-[#003366] mb-2">
                    Drag & drop .docx file here
                  </p>
                  <p className="text-muted-foreground mb-4">or click to browse</p>
                </>
              )}
            </div>
            <input
              id="file-input"
              type="file"
              accept=".docx"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            {/* Region and Assessment Type Selection */}
            {parsedFile && (
              <div className="space-y-4 pt-4 border-t animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Region Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="region" className="text-sm font-medium">
                      Region <span className="text-red-500">*</span>
                    </Label>
                    <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                      <SelectTrigger id="region" data-testid="region-select">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Assessment Type Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="assessment-type" className="text-sm font-medium">
                      Assessment Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={selectedAssessmentType} onValueChange={setSelectedAssessmentType}>
                      <SelectTrigger id="assessment-type" data-testid="assessment-type-select">
                        <SelectValue placeholder="Select assessment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSESSMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Create Assessment Button */}
                <Button
                  onClick={handleCreateAssessment}
                  disabled={!selectedRegion || !selectedAssessmentType || creating}
                  className="w-full h-12 bg-[#003366] hover:bg-[#003366]/90 text-white"
                  data-testid="create-assessment-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Assessment...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Create Assessment
                    </>
                  )}
                </Button>

                {creating && (
                  <Progress value={uploadProgress} className="w-full" />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Results */}
        {uploadResults.length > 0 && (
          <Card data-testid="upload-results">
            <CardHeader>
              <CardTitle className="text-[#003366]">Upload Results</CardTitle>
              <CardDescription>
                {uploadResults.filter(r => r.success).length} of {uploadResults.length} files processed successfully
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {uploadResults.map((result, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                    data-testid={`result-${index}`}
                  >
                    {/* Header with name and actions */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {result.success ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-lg">
                            {result.fullName || "Unknown"}
                          </p>
                          {result.success ? (
                            <div className="text-sm text-muted-foreground space-y-1">
                              <p>
                                <span className="text-muted-foreground/70">Assessment ID:</span>{" "}
                                <code className="bg-slate-100 px-1 rounded">{result.assessmentId || "Not provided"}</code>
                                {result.assessmentIdValidated && (
                                  <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Verified</Badge>
                                )}
                              </p>
                              <p className="truncate">
                                <span className="text-muted-foreground/70">Email:</span>{" "}
                                {result.email || "Not provided"}
                              </p>
                              <div className="flex gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {getRegionLabel(result.region)}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {getTypeLabel(result.assessmentType)}
                                </Badge>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-red-600">{result.error}</p>
                          )}
                        </div>
                      </div>
                      {result.success && result.uniqueToken && (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyLink(result.uniqueToken)}
                            className="gap-1"
                            data-testid={`copy-btn-${index}`}
                          >
                            <Copy className="w-4 h-4" />
                            Copy Assessment Link
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Account Credentials Section */}
                    {result.success && (
                      <div className="mt-4 pt-4 border-t border-green-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        {/* Talent Account */}
                        {result.talentAccount && (
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-semibold text-blue-800">
                                Talent Portal Account
                              </span>
                              {result.talentAccount.isNew && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">NEW</Badge>
                              )}
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground w-20">Email:</span>
                                <code className="bg-slate-100 px-2 py-0.5 rounded text-xs flex-1">
                                  {result.talentAccount.email}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => copyText(result.talentAccount.email, "Email")}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              {result.talentAccount.temporaryPassword && (
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground w-20">Password:</span>
                                  <code className="bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-xs font-mono flex-1">
                                    {result.talentAccount.temporaryPassword}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => copyText(result.talentAccount.temporaryPassword, "Password")}
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                              {!result.talentAccount.isNew && (
                                <p className="text-xs text-muted-foreground italic">
                                  Existing account - using previous credentials
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                Portal: <code className="bg-slate-100 px-1 rounded">{result.talentAccount.portalUrl}</code>
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Manager Account */}
                        {result.managerAccount && (
                          <div className={`bg-white rounded-lg p-3 border ${
                            result.managerAccount.linked ? 'border-green-200' : 'border-amber-200'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-semibold text-green-800">
                                Manager Account
                              </span>
                              {result.managerAccount.isNew && (
                                <Badge className="bg-green-100 text-green-700 text-xs">NEW</Badge>
                              )}
                              {!result.managerAccount.linked && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">NOT LINKED</Badge>
                              )}
                            </div>
                            {result.managerAccount.linked ? (
                              <div className="space-y-1 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground w-20">Name:</span>
                                  <span className="font-medium">{result.managerAccount.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground w-20">Email:</span>
                                  <code className="bg-slate-100 px-2 py-0.5 rounded text-xs flex-1">
                                    {result.managerAccount.email}
                                  </code>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => copyText(result.managerAccount.email, "Email")}
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                                {result.managerAccount.temporaryPassword && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground w-20">Password:</span>
                                    <code className="bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-xs font-mono flex-1">
                                      {result.managerAccount.temporaryPassword}
                                    </code>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyText(result.managerAccount.temporaryPassword, "Password")}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                                {!result.managerAccount.isNew && (
                                  <p className="text-xs text-muted-foreground italic">
                                    Existing account - using previous credentials
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-amber-600">
                                Manager email not provided in document. Add "Manager Email:" field to auto-create manager account.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Assessment Link */}
                    {result.success && result.assessmentUrl && (
                      <div className="mt-3 pt-3 border-t border-green-100">
                        <div className="flex items-center gap-2 text-sm">
                          <LinkIcon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Assessment Link:</span>
                          <code className="bg-slate-100 px-2 py-0.5 rounded text-xs flex-1">
                            {window.location.origin}{result.assessmentUrl}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2"
                            onClick={() => copyLink(result.uniqueToken)}
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                          <Link to={result.assessmentUrl} target="_blank">
                            <Button variant="ghost" size="sm" className="h-6 px-2">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Open
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-[#003366] text-lg">Document Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <FileText className="w-4 h-4 mt-0.5 text-[#B8860B]" />
                <span>Documents must be in .docx format (Microsoft Word)</span>
              </li>
              <li className="flex items-start gap-2">
                <FileText className="w-4 h-4 mt-0.5 text-[#B8860B]" />
                <span><strong>Required fields:</strong> Full Name, Employee Email, Assessment ID</span>
              </li>
              <li className="flex items-start gap-2">
                <FileText className="w-4 h-4 mt-0.5 text-[#B8860B]" />
                <span><strong>Manager fields:</strong> Direct Manager, Manager Email (for auto-creating manager account)</span>
              </li>
              <li className="flex items-start gap-2">
                <FileText className="w-4 h-4 mt-0.5 text-[#B8860B]" />
                <span><strong>Optional:</strong> Job Title, Department, Job Grade, Business Unit, Location</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 text-[#B8860B]" />
                <span>Assessment ID links multiple assessments (self, technical, simulation) to the same person</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 text-[#B8860B]" />
                <span>You must select Region and Assessment Type before creating the assessment</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
